import { useEffect, useId, useRef, useState } from 'react'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Minus, Plus, X } from 'lucide-react'
import { Dialog, EmptyState, formatMoney } from '@comforthub/design-system'
import { api } from '../../../src/lib/api'
import { apiErrorMessage } from '../../../src/lib/apiError'
import { formatOrderNr } from '../../../src/lib/orderNr'
import { ORDER_PAYMENT_STATUSES } from '../../../src/lib/optionSets'
import { TOKENS, Button } from '@comforthub/design-system'
import { ORDER_STATUSES } from './orderStatusDisplay'
import { linesVatCents } from './orderVat'

import type { OrderAppointment, OrderTargets } from './orderTargetDisplay'

export interface ManualOrder {
  id: string
  orderNr?: string
  customerName?: string | null
  customerId?: string | null
  amount?: number | null
  paymentStatus?: string | null
  status: string
  assignedTo?: string | null
  storeId?: string | null
  /** The outlet's name, as GET /orders returns it on each row. */
  storeName?: string | null
  readyBy?: string | null
  /** OrderDto.pickupAt: when it is ready and the courier or client comes for it. */
  pickupAt?: string | null
  pickupConfirmed?: boolean | null
  notes?: string | null
  type?: string | null
  targets?: OrderTargets | null
  appointment?: OrderAppointment | null
  calendarBound?: boolean | null
  quote?: { status?: string | null } | null
  /** Set when a subscription made the order (service-core OrderDto). */
  planId?: string | null
  planName?: string | null
  /** Its place in the plan: 2 in "2 of 4". */
  planDeliveryNumber?: number | null
  /** The plan's number of deliveries; null when it runs until cancelled. */
  planTotalDeliveries?: number | null
}

interface Worker {
  id: string
  name: string
}

interface StoreItem {
  id: string
  name: string
  isDeleted?: boolean
}

interface CustomerItem {
  id: string
  name?: string | null
  email?: string | null
}

/** A row of GET /inventory, as the picker reads it. */
interface CatalogueItem {
  id: string
  name: string
  type?: string | null
  /** The item's own price. */
  price?: number | null
  /** What customers pay through the item's active offerings (Price Source); absent when there are none or they disagree. */
  sellPriceEur?: number | null
  sellPriceVaries?: boolean | null
  /** Taxes option-set label, e.g. "24%". Absent when the item has no VAT. */
  vat?: string | null
}

/** One line of a new order. The unit price is kept as typed so it can be edited freely. */
interface OrderLine {
  inventoryId: string
  name: string
  quantity: number
  unitPrice: string
  /** Catalog taxes label copied when the line was picked. */
  vat?: string | null
}

const MIN_QUANTITY = 1
const MAX_QUANTITY = 99

function asNumber(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * The price a picked item starts at: what customers pay through its active offerings (the same
 * Price Source rule service-core uses for the cart), else the item's own price. The server stores
 * a manual line at whatever price it is sent, so the picker always sends the one it shows.
 */
function catalogueUnitPrice(item: CatalogueItem): number | null {
  return asNumber(item.sellPriceEur) ?? asNumber(item.price)
}

function clampQuantity(value: number): number {
  if (!Number.isFinite(value)) return MIN_QUANTITY
  return Math.min(MAX_QUANTITY, Math.max(MIN_QUANTITY, Math.round(value)))
}

/** Quantity × unit price, in whole cents so the lines add up to what they show. */
function lineTotalCents(line: OrderLine): number {
  return Math.round(line.quantity * (asNumber(line.unitPrice) ?? 0) * 100)
}

export function toLocalISOString(date: Date) {
  const tzoffset = date.getTimezoneOffset() * 60000
  return new Date(date.getTime() - tzoffset).toISOString().slice(0, 16)
}

export interface CreateOrderModalProps {
  open: boolean
  onClose: () => void
  editingOrder?: ManualOrder | null
  /** Called after a successful create or update. */
  onSuccess?: () => void
  /** API path prefix — defaults to merchant `/orders`. Worker app uses `/worker/orders`. */
  createPath?: string
}

export default function CreateOrderModal({
  open,
  onClose,
  editingOrder = null,
  onSuccess,
  createPath = '/orders',
}: CreateOrderModalProps) {
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [customerMode, setCustomerMode] = useState<'guest' | string>('guest')
  const [customerName, setCustomerName] = useState('')
  const [amount, setAmount] = useState(0)
  const [type, setType] = useState('One Off Purchase')
  const [paymentStatus, setPaymentStatus] = useState('')
  const [status, setStatus] = useState('not_started')
  const [assignedTo, setAssignedTo] = useState('')
  const [storeId, setStoreId] = useState('')
  const [readyBy, setReadyBy] = useState('')
  // What the field showed when the edit opened: an untouched field is not sent.
  const [readyByOpened, setReadyByOpened] = useState('')
  const [notes, setNotes] = useState('')
  const [formLoading, setFormLoading] = useState(false)
  const [lines, setLines] = useState<OrderLine[]>([])
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  // The one-store default is applied once per opening, when the stores arrive.
  const storeDefaultApplied = useRef(false)
  const fieldId = useId()

  const { data: workers = [] } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      const res = await api.get<{ content: Worker[] }>('/users', { params: { size: 200 } })
      return res.data.content || []
    },
    enabled: open,
  })

  const { data: storesData } = useQuery({
    queryKey: ['stores'],
    queryFn: async () => {
      const res = await api.get<{ content: StoreItem[] }>('/stores', { params: { size: 100 } })
      return (res.data.content || []).filter(s => !s.isDeleted)
    },
    enabled: open,
  })
  const stores = storesData ?? []

  const { data: customers = [] } = useQuery({
    queryKey: ['customers'],
    queryFn: async () => {
      const res = await api.get<{ content: CustomerItem[] }>('/customers', { params: { size: 200 } })
      return res.data.content || []
    },
    enabled: open,
  })

  // Search the catalogue 300 ms after typing stops.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  // Only a new order has the picker; editing an order's lines is not done here.
  const pickerEnabled = open && !editingOrder
  const catalogue = useQuery({
    queryKey: ['inventory', 'order-picker', debouncedSearch],
    queryFn: async () => {
      const res = await api.get<{ content: CatalogueItem[] }>('/inventory', {
        params: { search: debouncedSearch, page: 0, size: 20 },
      })
      // Events are left out: their tickets are sold per occurrence.
      return (res.data.content || []).filter(item => (item.type || '').trim().toLowerCase() !== 'event')
    },
    enabled: pickerEnabled,
    placeholderData: keepPreviousData,
  })

  const resetForCreate = () => {
    setCustomerMode('guest')
    setCustomerName('')
    setAmount(0)
    setType('One Off Purchase')
    setPaymentStatus('')
    setStatus('not_started')
    // Nobody is assigned until someone picks a person.
    setAssignedTo('')
    setStoreId('')
    storeDefaultApplied.current = false
    setReadyBy(toLocalISOString(new Date(Date.now() + 30 * 60 * 1000)))
    setNotes('')
    setLines([])
    setSearch('')
    setDebouncedSearch('')
    setError(null)
  }

  useEffect(() => {
    if (!open) return
    if (editingOrder) {
      if (editingOrder.customerId) {
        setCustomerMode(editingOrder.customerId)
      } else {
        setCustomerMode('guest')
      }
      setCustomerName(editingOrder.customerName || '')
      setAmount(editingOrder.amount || 0)
      setType(editingOrder.type || 'One Off Purchase')
      setPaymentStatus(editingOrder.paymentStatus || '')
      setStatus(editingOrder.status || 'not_started')
      setAssignedTo(editingOrder.assignedTo || '')
      setStoreId(editingOrder.storeId || '')
      // The pickup time, else the old prep target. Never readyBy: on older
      // orders it is the time placed, and saving it made that a deadline.
      const pickup = editingOrder.pickupAt || editingOrder.targets?.prep?.endAt || null
      const opened = pickup ? toLocalISOString(new Date(pickup)) : ''
      setReadyBy(opened)
      setReadyByOpened(opened)
      setNotes(editingOrder.notes || '')
    } else {
      resetForCreate()
    }
    // Reset only when the dialog opens or the order changes: lists that arrive later must not
    // wipe what was typed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editingOrder])

  // A company with exactly one store gets it pre-selected, once, if the field is still empty.
  useEffect(() => {
    if (!open || editingOrder || !storesData || storeDefaultApplied.current) return
    storeDefaultApplied.current = true
    if (storesData.length === 1) {
      const only = storesData[0].id
      setStoreId(current => current || only)
    }
  }, [open, editingOrder, storesData])

  const addLine = (item: CatalogueItem) => {
    setLines(current => {
      const existing = current.find(line => line.inventoryId === item.id)
      if (existing) {
        return current.map(line =>
          line.inventoryId === item.id ? { ...line, quantity: clampQuantity(line.quantity + 1) } : line,
        )
      }
      const price = catalogueUnitPrice(item)
      return [
        ...current,
        {
          inventoryId: item.id,
          name: item.name,
          quantity: 1,
          unitPrice: price != null ? price.toFixed(2) : '',
          vat: item.vat ?? null,
        },
      ]
    })
  }

  const updateLine = (inventoryId: string, change: Partial<OrderLine>) => {
    setLines(current => current.map(line => (line.inventoryId === inventoryId ? { ...line, ...change } : line)))
  }

  const removeLine = (inventoryId: string) => {
    setLines(current => current.filter(line => line.inventoryId !== inventoryId))
  }

  const hasLines = !editingOrder && lines.length > 0
  const linesTotal = lines.reduce((sum, line) => sum + lineTotalCents(line), 0) / 100
  const linesVat = linesVatCents(lines) / 100

  const submitMutation = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      if (editingOrder) {
        await api.put(`/orders/${editingOrder.id}`, payload)
      } else {
        await api.post(createPath, payload)
      }
    },
    onMutate: () => {
      setError(null)
      setFormLoading(true)
    },
    onError: (err: unknown) => {
      setError(apiErrorMessage(err, 'Order save failed'))
    },
    onSuccess: () => {
      onClose()
      queryClient.invalidateQueries({ queryKey: ['orders'] })
      onSuccess?.()
    },
    onSettled: () => {
      setFormLoading(false)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const guestName = customerName.trim()
    const isGuest = customerMode === 'guest'
    if ((isGuest && !guestName) || !storeId) return
    if (hasLines ? lines.some(line => asNumber(line.unitPrice) == null || Number(line.unitPrice) < 0) : !amount) return

    const payload: Record<string, unknown> = {
      // With lines, the server sets the amount to their sum; send the same sum.
      amount: hasLines ? linesTotal : Number(amount),
      type,
      paymentStatus,
      status,
      assignedTo: assignedTo || null,
      storeId: storeId || null,
      notes: notes || null,
    }
    // pickupAt is the new name; readyBy stays as its alias until every API is
    // on it. On an edit, only when the field was changed: the PUT would
    // otherwise re-send the time on every save and could confirm a suggested
    // time or tell the courier it moved when nobody touched it.
    if (!editingOrder || readyBy !== readyByOpened) {
      const iso = readyBy ? new Date(readyBy).toISOString() : editingOrder ? '' : null
      payload.pickupAt = iso
      payload.readyBy = iso
    }
    if (isGuest) {
      payload.customerName = guestName
    } else {
      payload.customerId = customerMode
      payload.customerName = customers.find(c => c.id === customerMode)?.name || guestName || null
    }
    if (hasLines) {
      // The lines go with the create, so an order is never left behind without them.
      payload.items = lines.map(line => ({
        inventoryId: line.inventoryId,
        quantity: line.quantity,
        unitPrice: Number(line.unitPrice),
      }))
    }

    submitMutation.mutate(payload)
  }

  if (!open) return null

  const catalogueItems = catalogue.data ?? []
  // A failed first load (no catalog:read, or the list is down) leaves the amount-only form.
  const catalogueFailed = catalogue.isError
  const showSearch = !catalogueFailed || debouncedSearch !== ''
  const ids = {
    items: `${fieldId}-items`,
    amount: `${fieldId}-amount`,
    status: `${fieldId}-status`,
    staff: `${fieldId}-staff`,
    store: `${fieldId}-store`,
    notes: `${fieldId}-notes`,
  }

  // Escape and the close button close it, but not while it saves; a stray click
  // outside doesn't, so a half-filled order isn't lost. Save errors show inside.
  return (
    <Dialog
      open={open}
      title={editingOrder ? `Edit order ${editingOrder.orderNr || formatOrderNr(editingOrder.id)}` : 'New Order'}
      error={error}
      onClose={onClose}
      busy={formLoading}
      dismissible={false}
      size="lg"
      footer={
        <>
          <Button type="button" onClick={onClose} variant="secondary" disabled={formLoading}>
            Cancel
          </Button>
          <Button type="submit" form="create-order-form" loading={formLoading} variant="primary">
            Save Order
          </Button>
        </>
      }
    >
        <form id="create-order-form" onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1">
            <label className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Client</label>
            <select
              value={customerMode}
              onChange={e => {
                setCustomerMode(e.target.value)
                if (e.target.value !== 'guest') {
                  const selected = customers.find(c => c.id === e.target.value)
                  setCustomerName(selected?.name || '')
                }
              }}
              className={TOKENS.input}
            >
              <option value="guest">Guest (walk-in / phone)</option>
              {customers.map(c => (
                <option key={c.id} value={c.id}>
                  {c.name || c.email || c.id}
                </option>
              ))}
            </select>
            {customerMode === 'guest' && (
              <input
                type="text"
                required
                placeholder="e.g. Sarah Jenkins"
                value={customerName}
                onChange={e => setCustomerName(e.target.value)}
                className={`${TOKENS.input} mt-2`}
              />
            )}
          </div>

          {!editingOrder && (
            <div className="space-y-2">
              <label htmlFor={ids.items} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Items</label>
              {showSearch && (
                <input
                  id={ids.items}
                  type="search"
                  placeholder="Search products and services"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className={TOKENS.input}
                />
              )}
              {catalogueFailed ? (
                <p role="status" className="text-sm text-foreground-muted">
                  {lines.length > 0
                    ? "Products can't be listed right now. Try again in a moment."
                    : "Products can't be listed here. Enter an amount instead."}
                </p>
              ) : catalogue.isPending ? (
                <p role="status" className="text-sm text-foreground-muted">Loading products…</p>
              ) : catalogueItems.length === 0 ? (
                <EmptyState
                  title={debouncedSearch ? `No products match "${debouncedSearch}"` : 'No products to add yet'}
                  text="You can still enter an amount instead."
                />
              ) : (
                <ul aria-label="Products" className="max-h-48 overflow-y-auto rounded-xl border border-border divide-y divide-border">
                  {catalogueItems.map(item => {
                    const price = catalogueUnitPrice(item)
                    return (
                      <li key={item.id}>
                        <button
                          type="button"
                          onClick={() => addLine(item)}
                          aria-label={`Add ${item.name}`}
                          className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm text-foreground hover:bg-surface-hover"
                        >
                          <span className="min-w-0">
                            <span className="block truncate font-medium">{item.name}</span>
                            <span className="block text-xs text-foreground-muted">
                              {item.type || 'Product'}
                              {item.sellPriceVaries ? ' · price varies by offering' : ''}
                            </span>
                          </span>
                          <span className="shrink-0 font-semibold">{price != null ? formatMoney(price) : 'No price'}</span>
                        </button>
                      </li>
                    )
                  })}
                </ul>
              )}

              {lines.length > 0 && (
                <ul aria-label="Order items" className="space-y-2">
                  {lines.map(line => (
                    <li
                      key={line.inventoryId}
                      className="flex flex-wrap items-center gap-2 rounded-xl border border-border bg-surface-raised px-3 py-2"
                    >
                      <span className="min-w-0 flex-1 truncate text-sm font-medium text-foreground">{line.name}</span>
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          aria-label={`Fewer ${line.name}`}
                          disabled={line.quantity <= MIN_QUANTITY}
                          onClick={() => updateLine(line.inventoryId, { quantity: clampQuantity(line.quantity - 1) })}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground-muted hover:bg-surface-hover disabled:opacity-50"
                        >
                          <Minus size={14} aria-hidden="true" />
                        </button>
                        <input
                          type="number"
                          min={MIN_QUANTITY}
                          max={MAX_QUANTITY}
                          step={1}
                          aria-label={`Quantity of ${line.name}`}
                          value={line.quantity}
                          onChange={e => updateLine(line.inventoryId, { quantity: clampQuantity(Number(e.target.value)) })}
                          className="w-14 rounded-lg border border-border bg-surface px-2 py-1 text-center text-sm text-foreground"
                        />
                        <button
                          type="button"
                          aria-label={`More ${line.name}`}
                          disabled={line.quantity >= MAX_QUANTITY}
                          onClick={() => updateLine(line.inventoryId, { quantity: clampQuantity(line.quantity + 1) })}
                          className="flex h-8 w-8 items-center justify-center rounded-lg border border-border text-foreground-muted hover:bg-surface-hover disabled:opacity-50"
                        >
                          <Plus size={14} aria-hidden="true" />
                        </button>
                      </div>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        required
                        aria-label={`Unit price of ${line.name} (€)`}
                        value={line.unitPrice}
                        onChange={e => updateLine(line.inventoryId, { unitPrice: e.target.value })}
                        className="w-24 rounded-lg border border-border bg-surface px-2 py-1 text-right text-sm text-foreground"
                      />
                      <span className="w-20 text-right text-sm font-semibold text-foreground">
                        {formatMoney(lineTotalCents(line) / 100)}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove ${line.name}`}
                        onClick={() => removeLine(line.inventoryId)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-foreground-muted hover:bg-surface-hover"
                      >
                        <X size={14} aria-hidden="true" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {hasLines ? (
              <div className="space-y-1">
                <span id={ids.amount} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Amount</span>
                <output aria-labelledby={ids.amount} className="block py-2.5 text-sm font-semibold text-foreground">
                  Total {formatMoney(linesTotal)}
                </output>
                {linesVat > 0 ? (
                  <p className="text-xs text-foreground-muted">of which VAT {formatMoney(linesVat)}</p>
                ) : null}
                <p className="text-xs text-foreground-muted">The sum of the items. Change a unit price to charge a different amount.</p>
              </div>
            ) : (
              <div className="space-y-1">
                <label htmlFor={ids.amount} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Amount</label>
                <input
                  id={ids.amount}
                  type="number"
                  step="0.01"
                  required
                  min="0.01"
                  placeholder="0.00"
                  value={amount || ''}
                  onChange={e => setAmount(Number(e.target.value))}
                  className={TOKENS.input}
                />
              </div>
            )}

            <div className="space-y-1">
              <label className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Order Type</label>
              <select value={type} onChange={e => setType(e.target.value)} className={TOKENS.input}>
                <option value="One Off Purchase">One Off Purchase</option>
                <option value="Plan Purchase">Plan Purchase</option>
                <option value="Plan Delivery">Plan Delivery</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Payment Status</label>
              <select
                value={paymentStatus}
                onChange={e => setPaymentStatus(e.target.value)}
                className={TOKENS.input}
              >
                <option value="">Unpaid / not set</option>
                {ORDER_PAYMENT_STATUSES.map(ps => (
                  <option key={ps} value={ps}>{ps}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor={ids.status} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Workflow Status</label>
              <select id={ids.status} value={status} onChange={e => setStatus(e.target.value)} className={TOKENS.input}>
                {ORDER_STATUSES.map(entry => (
                  <option key={entry.status} value={entry.status}>{entry.label}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label htmlFor={ids.staff} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Assign Staff</label>
              <select id={ids.staff} value={assignedTo} onChange={e => setAssignedTo(e.target.value)} className={TOKENS.input}>
                <option value="">Unassigned</option>
                {workers.map(w => (
                  <option key={w.id} value={w.id}>{w.name}</option>
                ))}
              </select>
            </div>

            <div className="space-y-1">
              <label htmlFor={ids.store} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Store</label>
              <select
                id={ids.store}
                required
                value={storeId}
                onChange={e => setStoreId(e.target.value)}
                className={TOKENS.input}
              >
                <option value="">Select store</option>
                {stores.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="space-y-1">
            <label className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Pickup time</label>
            <input
              type="datetime-local"
              value={readyBy}
              onChange={e => setReadyBy(e.target.value)}
              className={TOKENS.input}
            />
          </div>

          <div className="space-y-1">
            <label htmlFor={ids.notes} className={`text-xs font-semibold ${TOKENS.textSecondary}`}>Notes</label>
            <textarea
              id={ids.notes}
              rows={2}
              placeholder="e.g. Allergies details, packaging preferences..."
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className={TOKENS.input}
            />
          </div>
        </form>
    </Dialog>
  )
}
